import { SchemaParty } from '../../api/api-schema';
import { ElectionType } from '../../api/api-types';

type Translate = (key: string, values?: Record<string, unknown>) => string;

export type PartyListParty = SchemaParty & {
  voteCount2?: number;
};

export const PartySortBy = {
  Name: 'name',
  Votes: 'votes',
  AlsoEuroparliament: 'also-europarliament',
  ExpirationDate: 'expiration-date',
  Folketingsvalg: 'folketingsvalg',
  EuropaParlamentsvalg: 'europa-parlamentsvalg',
} as const;

export type PartyListSortBy =
  (typeof PartySortBy)[keyof typeof PartySortBy];

export type SortDirection = 'asc' | 'desc';

export type ElectionBadge = {
  label: string;
  title: string;
};

export type PartySortOption = {
  value: PartyListSortBy;
  labelKey?: string;
  label?: string;
};

const nameCompare = (a: PartyListParty, b: PartyListParty) =>
  a.name?.localeCompare(b.name || '') ?? 0;

const supportsElection = (party: PartyListParty, electionType: ElectionType) =>
  party.type === electionType || party.type === ElectionType.BothTypes;

const partySortComparators: Record<
  PartyListSortBy,
  (a: PartyListParty, b: PartyListParty) => number
> = {
  [PartySortBy.Name]: nameCompare,
  [PartySortBy.Votes]: (a, b) => (b.voteCount ?? 0) - (a.voteCount ?? 0),
  [PartySortBy.AlsoEuroparliament]: (a, b) => (b.type ?? 0) - (a.type ?? 0),
  [PartySortBy.ExpirationDate]: (a, b) =>
    new Date(a.expiryDate || 0).getTime() -
    new Date(b.expiryDate || 0).getTime(),
  [PartySortBy.Folketingsvalg]: (a, b) =>
    Number(supportsElection(b, ElectionType.Folketingsvalg)) -
    Number(supportsElection(a, ElectionType.Folketingsvalg)),
  [PartySortBy.EuropaParlamentsvalg]: (a, b) =>
    Number(supportsElection(b, ElectionType.EuropaParlamentsvalg)) -
    Number(supportsElection(a, ElectionType.EuropaParlamentsvalg)),
};

export const partySortOptions: PartySortOption[] = [
  { value: PartySortBy.Name, labelKey: 'parties.partiesName' },
  { value: PartySortBy.Votes, label: 'Antal vælgererklæringer' },
  {
    value: PartySortBy.ExpirationDate,
    labelKey: 'parties.partiesExpirationDate',
  },
  { value: PartySortBy.Folketingsvalg, label: 'Folketingsvalg' },
  {
    value: PartySortBy.EuropaParlamentsvalg,
    label: 'Europa-Parlamentsvalg',
  },
];

export const partyTableSortOptions: PartySortOption[] = [
  { value: PartySortBy.Name, labelKey: 'parties.partiesName' },
  { value: PartySortBy.Votes, labelKey: 'parties.partiesVotes' },
  {
    value: PartySortBy.ExpirationDate,
    labelKey: 'parties.partiesExpirationDate',
  },
  {
    value: PartySortBy.AlsoEuroparliament,
    labelKey: 'parties.partiesAlsoEuroparliment',
  },
];

export type PartyTableSortOption = PartySortOption;

export function getPartySortOptionLabel(option: PartySortOption, t: Translate) {
  return option.labelKey ? t(option.labelKey) : option.label || '';
}

export function mergePartyElectionTypes(
  parties: SchemaParty[] = [],
): PartyListParty[] {
  const partiesById = parties.reduce<Record<string, SchemaParty[]>>(
    (groups, party) => {
      if (!party.id) return groups;

      return {
        ...groups,
        [party.id]: [...(groups[party.id] || []), party],
      };
    },
    {},
  );

  return Object.values(partiesById).map((group) => {
    const folketingParty = group.find(
      (party) => party.type === ElectionType.Folketingsvalg,
    );
    const europeanParty = group.find(
      (party) => party.type === ElectionType.EuropaParlamentsvalg,
    );

    if (!folketingParty || !europeanParty) {
      return group[0];
    }

    return {
      ...folketingParty,
      type: ElectionType.BothTypes,
      active: group.some((party) => party.active),
      expiryDate: folketingParty.expiryDate,
      expiryDate2: europeanParty.expiryDate,
      voteCount: folketingParty.voteCount,
      voteCount2: europeanParty.voteCount,
    };
  });
}

export function sortParties(
  items: PartyListParty[],
  sorting: PartyListSortBy,
  sortDirection: SortDirection,
) {
  const direction = sortDirection === 'asc' ? 1 : -1;
  const compare = partySortComparators[sorting] || nameCompare;

  return items.concat().sort((a, b) => {
    const diff = direction * compare(a, b);
    if (diff !== 0) return diff;

    return direction * nameCompare(a, b);
  });
}
